/**
 * Fluent builder for constructing OData filter expressions
 *
 * @example
 * const filter = new ODataFilterBuilder()
 *   .equals('status', 'Active')
 *   .greaterThan('createdDate', new Date('2024-01-01'))
 *   .contains('name', 'meter');
 */

export class ODataFilterBuilder {
  private readonly filters: string[] = [];

  /**
   * Add equality condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare (supports strings, numbers, booleans, Dates, and GUIDs)
   * @returns The filter builder instance for chaining
   */
  public equals(field: string, value: string | number | boolean | Date): this {
    // Handle Date objects - convert to ISO 8601
    if (value instanceof Date) {
      this.filters.push(`${field} eq ${value.toISOString()}`);
      return this;
    }

    if (typeof value === 'string') {
      // Check if it's a GUID (simple pattern check)
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        this.filters.push(`${field} eq ${value}`);
      } else {
        this.filters.push(`${field} eq '${value}'`);
      }
    } else {
      this.filters.push(`${field} eq ${value}`);
    }
    return this;
  }

  /**
   * Add date equality condition (matches entire day regardless of time)
   *
   * @param field - The DateTimeOffset field name
   * @param date - The date to match
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.dateOnly('occurrence', new Date('2022-01-01'))
   * // Result: occurrence ge 2022-01-01T00:00:00.000Z and occurrence le 2022-01-01T23:59:59.999Z
   */
  public dateOnly(field: string, date: Date): this {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    this.greaterThanOrEqual(field, startOfDay);
    this.lessThanOrEqual(field, endOfDay);

    return this;
  }

  /**
   * Add multiple equality conditions from an object (useful for form values)
   * Automatically skips null, undefined, and empty string values
   *
   * @param obj - Object with key-value pairs to convert to equality filters
   * @returns The filter builder instance for chaining
   *
   * @example
   * // From FormGroup
   * const formValues = this.searchForm.getRawValue();
   * filter.fromObject(formValues)
   *
   * @example
   * // From plain object
   * filter.fromObject({
   *   status: 'Active',
   *   type: 'Consumption',
   *   name: '',  // skipped
   *   id: null   // skipped
   * })
   * // Result: status eq 'Active' and type eq 'Consumption'
   */
  public fromObject(obj: Record<string, any>): this {
    for (const [key, value] of Object.entries(obj)) {
      // Skip null, undefined, and empty strings
      if (value !== null && value !== undefined && value !== '') {
        this.equals(key, value);
      }
    }
    return this;
  }

  /**
   * Add not equals condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare against
   * @returns The filter builder instance for chaining
   */
  public notEquals(field: string, value: string | number | boolean | Date): this {
    // Handle Date objects - convert to ISO 8601
    if (value instanceof Date) {
      this.filters.push(`${field} ne ${value.toISOString()}`);
      return this;
    }

    if (typeof value === 'string') {
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        this.filters.push(`${field} ne ${value}`);
      } else {
        this.filters.push(`${field} ne '${value}'`);
      }
    } else {
      this.filters.push(`${field} ne ${value}`);
    }
    return this;
  }
  /**
   * Add greater than condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare (number or Date)
   * @returns The filter builder instance for chaining
   *
   * @example
   * // Number comparison
   * filter.greaterThan('price', 100)
   * // Result: price gt 100
   *
   * @example
   * // Date comparison
   * filter.greaterThan('createdDate', new Date('2024-01-01'))
   * // Result: createdDate gt 2024-01-01T00:00:00.000Z
   */
  public greaterThan(field: string, value: number | Date): this {
    const formattedValue = value instanceof Date ? value.toISOString() : value;
    this.filters.push(`${field} gt ${formattedValue}`);
    return this;
  }

  /**
   * Add greater than or equal condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare (number or Date)
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.greaterThanOrEqual('quantity', 10)
   * // Result: quantity ge 10
   */
  public greaterThanOrEqual(field: string, value: number | Date): this {
    const formattedValue = value instanceof Date ? value.toISOString() : value;
    this.filters.push(`${field} ge ${formattedValue}`);
    return this;
  }

  /**
   * Add less than condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare (number or Date)
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.lessThan('price', 500)
   * // Result: price lt 500
   */
  public lessThan(field: string, value: number | Date): this {
    const formattedValue = value instanceof Date ? value.toISOString() : value;
    this.filters.push(`${field} lt ${formattedValue}`);
    return this;
  }

  /**
   * Add less than or equal condition
   *
   * @param field - The field name to filter on
   * @param value - The value to compare (number or Date)
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.lessThanOrEqual('createdDate', new Date())
   * // Result: createdDate le 2024-01-26T12:00:00.000Z
   */
  public lessThanOrEqual(field: string, value: number | Date): this {
    const formattedValue = value instanceof Date ? value.toISOString() : value;
    this.filters.push(`${field} le ${formattedValue}`);
    return this;
  }

  /**
   * Add contains condition (substring search)
   *
   * @param field - The field name to search in
   * @param value - The substring to search for
   * @param caseSensitive - Whether the search should be case-sensitive (default: false)
   * @returns The filter builder instance for chaining
   *
   * @example
   * // Case-insensitive search (default)
   * filter.contains('name', 'meter')
   * // Result: contains(tolower(name), 'meter')
   *
   * @example
   * // Case-sensitive search
   * filter.contains('name', 'Meter', true)
   * // Result: contains(name, 'Meter')
   */
  public contains(field: string, value: string, caseSensitive: boolean = false): this {
    const fieldExpr = caseSensitive ? field : `tolower(${field})`;
    const valueExpr = caseSensitive ? `'${value}'` : `'${value.toLowerCase()}'`;
    this.filters.push(`contains(${fieldExpr}, ${valueExpr})`);
    return this;
  }

  /**
   * Add starts with condition
   *
   * @param field - The field name to filter on
   * @param value - The prefix to match
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.startsWith('name', 'MP')
   * // Result: startswith(name, 'MP')
   */
  public startsWith(field: string, value: string): this {
    this.filters.push(`startswith(${field}, '${value}')`);
    return this;
  }

  /**
   * Add ends with condition
   *
   * @param field - The field name to filter on
   * @param value - The suffix to match
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.endsWith('email', '@example.com')
   * // Result: endswith(email, '@example.com')
   */
  public endsWith(field: string, value: string): this {
    this.filters.push(`endswith(${field}, '${value}')`);
    return this;
  }

  /**
   * Add IN condition (field matches any value in array)
   *
   * @param field - The field name to filter on
   * @param values - Array of values to match against
   * @returns The filter builder instance for chaining
   *
   * @example
   * // String values
   * filter.in('status', ['Active', 'Pending', 'InProgress'])
   * // Result: status in ('Active', 'Pending', 'InProgress')
   *
   * @example
   * // Number values
   * filter.in('priority', [1, 2, 3])
   * // Result: priority in (1, 2, 3)
   *
   * @example
   * // GUID values (automatically detected)
   * filter.in('id', ['14c36070-b2c1-43cf-856c-0d60c81a68e9', '87654321-dcba-4321-dcba-210987654321'])
   * // Result: id in (14c36070-b2c1-43cf-856c-0d60c81a68e9, 87654321-dcba-4321-dcba-210987654321)
   */
  public in(field: string, values: (string | number)[]): this {
    const formattedValues = values.map(v => {
      if (typeof v === 'string') {
        // Check if GUID
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
          return v;
        }
        return `'${v}'`;
      }
      return v;
    }).join(', ');
    this.filters.push(`${field} in (${formattedValues})`);
    return this;
  }

  /**
   * Add is null condition
   *
   * @param field - The field name to check
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.isNull('deletedDate')
   * // Result: deletedDate eq null
   */
  public isNull(field: string): this {
    this.filters.push(`${field} eq null`);
    return this;
  }

  /**
   * Add is not null condition
   *
   * @param field - The field name to check
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.isNotNull('meteringPointId')
   * // Result: meteringPointId ne null
   */
  public isNotNull(field: string): this {
    this.filters.push(`${field} ne null`);
    return this;
  }

  /**
   * Combine with another filter using OR logic
   *
   * @param filterBuilder - Another filter builder to combine with OR
   * @returns The filter builder instance for chaining
   *
   * @example
   * const activeFilter = new ODataFilterBuilder().equals('status', 'Active');
   * const pendingFilter = new ODataFilterBuilder().equals('status', 'Pending');
   *
   * const combinedFilter = new ODataFilterBuilder()
   *   .or(activeFilter)
   *   .or(pendingFilter)
   *   .greaterThan('createdDate', new Date('2024-01-01'));
   * // Result: (status eq 'Active') or (status eq 'Pending') and createdDate gt 2024-01-01T00:00:00.000Z
   */
  public or(filterBuilder: ODataFilterBuilder): this {
    if (filterBuilder.filters.length > 0) {
      const orExpression = filterBuilder.filters.join(' and ');
      this.filters.push(`(${orExpression})`);
    }
    return this;
  }

  /**
   * Add a raw filter expression (for advanced scenarios)
   *
   * @param expression - Raw OData filter expression
   * @returns The filter builder instance for chaining
   *
   * @example
   * filter.raw("startswith(name, 'MP') and endswith(type, 'Meter')")
   * // Result: startswith(name, 'MP') and endswith(type, 'Meter')
   */
  public raw(expression: string): this {
    this.filters.push(expression);
    return this;
  }

  /**
   * Build the final filter string
   *
   * @returns The complete OData filter expression
   *
   * @example
   * const filter = new ODataFilterBuilder()
   *   .equals('status', 'Active')
   *   .greaterThan('price', 100);
   *
   * const filterString = filter.build();
   * // Result: "status eq 'Active' and price gt 100"
   */
  public build(): string {
    return this.filters.join(' and ');
  }

  /**
   * Convert to string (alias for build)
   *
   * @returns The complete OData filter expression
   */
  public toString(): string {
    return this.build();
  }

  /**
   * Check if filter has any conditions
   *
   * @returns True if no conditions have been added
   *
   * @example
   * const filter = new ODataFilterBuilder();
   * console.log(filter.isEmpty()); // true
   *
   * filter.equals('status', 'Active');
   * console.log(filter.isEmpty()); // false
   */
  public isEmpty(): boolean {
    return this.filters.length === 0;
  }
}
